import { SECTOR_TEMPLATES, type SectorId } from '@myplayer/schema';
import { IconCards, IconFilter, IconSearch, IconSort, IconTable } from '@myplayer/ui';
import { useState, type ReactElement, type ReactNode } from 'react';
import type { WorkspaceOption } from '../../auth-context.js';
import { cn } from '../../lib/utils.js';
import type { ProductFacetCounts, ProductSortOption } from '../../products-helpers.js';
import { Button, FilterClearButton, Input, Select } from './primitives.js';

export type ProductStatusFilter =
  'ALL' | 'LIVE' | 'SCHEDULED' | 'NEEDS_ATTENTION' | 'DRAFT' | 'PENDING' | 'INVALID' | 'RECALLED';
export type ProductValidationFilter = 'ALL' | 'VALID' | 'PENDING' | 'INVALID' | 'WARNING';

export interface SearchSortOption {
  readonly value: string;
  readonly label: string;
}

export const PRODUCT_SORT_OPTIONS: readonly SearchSortOption[] = [
  { value: 'MODIFIED_DESC', label: 'Modified (Newest first)' },
  { value: 'MODIFIED_ASC', label: 'Modified (Oldest first)' },
  { value: 'TITLE_ASC', label: 'Product (A–Z)' },
  { value: 'TITLE_DESC', label: 'Product (Z–A)' },
  { value: 'STATUS', label: 'Status' },
];

export const PRODUCT_STATUS_FILTER_OPTIONS = [
  ['ALL', 'All statuses'],
  ['LIVE', 'Live'],
  ['SCHEDULED', 'Scheduled'],
  ['NEEDS_ATTENTION', 'Needs attention'],
] as const;

export const PRODUCT_VALIDATION_FILTER_OPTIONS = [
  ['ALL', 'All validation states'],
  ['VALID', 'Valid'],
  ['PENDING', 'Pending'],
  ['INVALID', 'Invalid'],
  ['WARNING', 'Warning'],
] as const;

export interface SearchFilterBarProps {
  readonly searchId?: string | undefined;
  readonly searchAriaLabel: string;
  readonly searchPlaceholder: string;
  readonly searchQuery: string;
  readonly onSearchChange: (nextQuery: string) => void;
  readonly clearSearchAriaLabel?: string | undefined;
  readonly sortId?: string | undefined;
  readonly sortAriaLabel?: string | undefined;
  readonly sortValue?: string | undefined;
  readonly onSortChange?: ((nextSort: string) => void) | undefined;
  readonly sortOptions?: readonly SearchSortOption[] | undefined;
  readonly viewMode?: 'table' | 'cards' | undefined;
  readonly onToggleViewMode?: ((nextMode: 'table' | 'cards') => void) | undefined;
  readonly hasActiveFilters: boolean;
  readonly hasActiveFacetFilters?: boolean | undefined;
  readonly onClearFilters: () => void;
  readonly clearButtonTestId?: string | undefined;
  readonly defaultFilterDrawerOpen?: boolean | undefined;
  readonly 'data-testid'?: string | undefined;
  readonly role?: string | undefined;
  readonly 'aria-label'?: string | undefined;
  readonly className?: string | undefined;
  readonly children?: ReactNode | undefined;
}

/**
 * Shared two-tier Search & Filter Bar used across the Admin Console
 * (Products Table, Products Cards, Audit Log).
 *
 * Top row:
 * - Full-width search input with leading search icon and clear button
 * - Sort dropdown with leading sort icon
 * - Filter drawer toggle button (funnel icon) that opens/closes the bottom facet row
 * - Optional view-mode switcher button
 *
 * Bottom row (collapsible facet filter drawer):
 * - Facet filter dropdowns (`Brand`, `Status`, `Sector`, `Validation`, `Workspace`, `Resource`, `Actor`, `Action`, etc.)
 * - Right-aligned `Clear` button
 */
export function SearchFilterBar({
  searchId,
  searchAriaLabel,
  searchPlaceholder,
  searchQuery,
  onSearchChange,
  clearSearchAriaLabel = 'Clear search',
  sortId,
  sortAriaLabel = 'Sort by',
  sortValue,
  onSortChange,
  sortOptions,
  viewMode,
  onToggleViewMode,
  hasActiveFilters,
  hasActiveFacetFilters = false,
  onClearFilters,
  clearButtonTestId,
  defaultFilterDrawerOpen = true,
  'data-testid': dataTestId = 'search-filter-bar',
  role = 'search',
  'aria-label': ariaLabel,
  className,
  children,
}: SearchFilterBarProps): ReactElement {
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(defaultFilterDrawerOpen);
  const filterDrawerHighlighted = hasActiveFacetFilters;

  return (
    <div
      role={role}
      aria-label={ariaLabel}
      data-testid={dataTestId}
      className={cn(
        'overflow-visible rounded-card border border-border bg-surface shadow-card-base',
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-2 p-2.5">
        <div className="relative min-w-[160px] flex-1 sm:min-w-[220px]">
          <IconSearch className="pointer-events-none absolute left-3 top-2.5 text-fg-muted" />
          <Input
            id={searchId}
            aria-label={searchAriaLabel}
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && searchQuery.length > 0) {
                onSearchChange('');
              }
            }}
            className="pl-9 pr-8"
          />
          {searchQuery.length > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={clearSearchAriaLabel}
              onClick={() => onSearchChange('')}
              className="absolute right-2.5 top-2 h-5 w-5 rounded-full text-fg-muted hover:bg-surface hover:text-fg"
            >
              &times;
            </Button>
          )}
        </div>

        {sortOptions !== undefined &&
          sortOptions.length > 0 &&
          sortValue !== undefined &&
          onSortChange !== undefined && (
            <Select
              id={sortId}
              aria-label={sortAriaLabel}
              searchable={false}
              leadingIcon={<IconSort />}
              value={sortValue}
              onChange={(e) => onSortChange(e.target.value)}
              className="min-w-[160px] sm:min-w-[205px]"
            >
              {sortOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          )}

        {children !== undefined && (
          <Button
            type="button"
            variant="secondary"
            size="icon"
            data-testid="toggle-filter-drawer-btn"
            aria-label="Toggle filters"
            aria-expanded={isFilterDrawerOpen}
            title={isFilterDrawerOpen ? 'Hide filter options' : 'Show filter options'}
            onClick={() => setIsFilterDrawerOpen((prev) => !prev)}
            className={
              filterDrawerHighlighted ? 'border-brand bg-brand-wash text-brand' : undefined
            }
          >
            <IconFilter />
          </Button>
        )}

        {viewMode !== undefined && onToggleViewMode !== undefined && (
          <Button
            type="button"
            variant="secondary"
            size="icon"
            data-testid="search-bar-view-mode-btn"
            aria-label={viewMode === 'table' ? 'Switch to card view' : 'Switch to table view'}
            title={viewMode === 'table' ? 'Switch to card view' : 'Switch to table view'}
            onClick={() => onToggleViewMode(viewMode === 'table' ? 'cards' : 'table')}
          >
            {viewMode === 'table' ? <IconCards /> : <IconTable />}
          </Button>
        )}
      </div>

      {children !== undefined && (
        <div
          data-testid="search-filter-drawer"
          className={
            isFilterDrawerOpen
              ? 'flex flex-wrap items-center gap-2 border-t border-subtle px-2.5 py-2'
              : 'sr-only'
          }
        >
          <div className="flex flex-wrap items-center gap-2">{children}</div>
          <FilterClearButton
            active={hasActiveFilters}
            onClick={onClearFilters}
            data-testid={clearButtonTestId}
            className="ml-auto"
          />
        </div>
      )}
    </div>
  );
}

export interface ProductsSearchFilterBarProps {
  readonly idPrefix?: string | undefined;
  readonly ariaLabel?: string | undefined;
  readonly searchId?: string | undefined;
  readonly clearSearchAriaLabel?: string | undefined;
  readonly searchQuery: string;
  readonly onSearchChange: (q: string) => void;
  readonly brandFilter?: string | undefined;
  readonly setBrandFilter?: ((brand: string) => void) | undefined;
  readonly statusFilter?: ProductStatusFilter | undefined;
  readonly setStatusFilter?: ((status: ProductStatusFilter) => void) | undefined;
  readonly sectorFilter?: ('ALL' | SectorId) | undefined;
  readonly setSectorFilter?: ((sector: 'ALL' | SectorId) => void) | undefined;
  readonly validationFilter?: ProductValidationFilter | undefined;
  readonly setValidationFilter?: ((val: ProductValidationFilter) => void) | undefined;
  readonly workspaceFilter?: string | undefined;
  readonly setWorkspaceFilter?: ((wsId: string) => void) | undefined;
  readonly availableWorkspaces?: readonly WorkspaceOption[] | undefined;
  readonly sortBy?: ProductSortOption | undefined;
  readonly onSortChange?: ((next: ProductSortOption) => void) | undefined;
  readonly viewMode?: 'table' | 'cards' | undefined;
  readonly setViewMode?: ((mode: 'table' | 'cards') => void) | undefined;
  readonly facetCounts: ProductFacetCounts;
  readonly onFilterChangeResetScroll?: (() => void) | undefined;
}

export function ProductsSearchFilterBar({
  idPrefix = 'filter-product',
  ariaLabel = 'Products search and filters',
  searchId,
  clearSearchAriaLabel,
  searchQuery,
  onSearchChange,
  brandFilter = 'ALL',
  setBrandFilter,
  statusFilter = 'ALL',
  setStatusFilter,
  sectorFilter = 'ALL',
  setSectorFilter,
  validationFilter = 'ALL',
  setValidationFilter,
  workspaceFilter = 'ALL',
  setWorkspaceFilter,
  availableWorkspaces,
  sortBy = 'MODIFIED_DESC',
  onSortChange,
  viewMode,
  setViewMode,
  facetCounts,
  onFilterChangeResetScroll,
}: ProductsSearchFilterBarProps): ReactElement {
  const hasActiveFacetFilters =
    brandFilter !== 'ALL' ||
    statusFilter !== 'ALL' ||
    sectorFilter !== 'ALL' ||
    validationFilter !== 'ALL' ||
    workspaceFilter !== 'ALL';
  const hasActiveFilters = searchQuery.trim().length > 0 || hasActiveFacetFilters;

  const handleClearFilters = (): void => {
    onSearchChange('');
    setBrandFilter?.('ALL');
    setStatusFilter?.('ALL');
    setSectorFilter?.('ALL');
    setValidationFilter?.('ALL');
    setWorkspaceFilter?.('ALL');
    onFilterChangeResetScroll?.();
  };

  return (
    <SearchFilterBar
      data-testid="products-search-bar"
      aria-label={ariaLabel}
      searchId={searchId}
      searchAriaLabel="Search by GTIN"
      searchPlaceholder="Search products..."
      clearSearchAriaLabel={clearSearchAriaLabel}
      searchQuery={searchQuery}
      onSearchChange={(q) => {
        onSearchChange(q);
        onFilterChangeResetScroll?.();
      }}
      sortId={`${idPrefix}-sort`}
      sortAriaLabel="Sort products"
      sortValue={sortBy}
      onSortChange={(next) => {
        onSortChange?.(next as ProductSortOption);
        onFilterChangeResetScroll?.();
      }}
      sortOptions={PRODUCT_SORT_OPTIONS}
      viewMode={viewMode}
      onToggleViewMode={setViewMode}
      hasActiveFilters={hasActiveFilters}
      hasActiveFacetFilters={hasActiveFacetFilters}
      onClearFilters={handleClearFilters}
      clearButtonTestId="clear-product-filters-btn"
    >
      {setBrandFilter !== undefined && (
        <Select
          id={`${idPrefix}-brand`}
          aria-label="Filter by brand"
          filterLabel="Brand"
          optionCounts={facetCounts.brandCounts}
          className="min-w-[140px]"
          value={brandFilter}
          onChange={(e) => {
            setBrandFilter(e.target.value);
            onFilterChangeResetScroll?.();
          }}
        >
          <option value="ALL">All brands</option>
          {facetCounts.distinctBrands.map((brand) => (
            <option key={brand} value={brand}>
              {brand}
            </option>
          ))}
        </Select>
      )}
      {setStatusFilter !== undefined && (
        <Select
          id={`${idPrefix}-status`}
          aria-label="Filter by status"
          filterLabel="Status"
          optionCounts={facetCounts.statusCounts}
          className="min-w-[140px]"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as ProductStatusFilter);
            onFilterChangeResetScroll?.();
          }}
        >
          {PRODUCT_STATUS_FILTER_OPTIONS.map(([val, label]) => (
            <option key={val} value={val}>
              {label}
            </option>
          ))}
        </Select>
      )}
      {setSectorFilter !== undefined && (
        <Select
          id={`${idPrefix}-sector`}
          aria-label="Filter by sector"
          filterLabel="Sector"
          optionCounts={facetCounts.sectorCounts}
          className="min-w-[140px]"
          value={sectorFilter}
          onChange={(e) => {
            setSectorFilter(e.target.value as 'ALL' | SectorId);
            onFilterChangeResetScroll?.();
          }}
        >
          <option value="ALL">All sectors</option>
          {Object.values(SECTOR_TEMPLATES).map((sec) => (
            <option key={sec.sectorId} value={sec.sectorId}>
              {sec.name}
            </option>
          ))}
        </Select>
      )}
      {setValidationFilter !== undefined && (
        <Select
          id={`${idPrefix}-validation`}
          aria-label="Filter by validation"
          filterLabel="Validation"
          optionCounts={facetCounts.validationCounts}
          className="min-w-[145px]"
          value={validationFilter}
          onChange={(e) => {
            setValidationFilter(e.target.value as ProductValidationFilter);
            onFilterChangeResetScroll?.();
          }}
        >
          {PRODUCT_VALIDATION_FILTER_OPTIONS.map(([val, label]) => (
            <option key={val} value={val}>
              {label}
            </option>
          ))}
        </Select>
      )}
      {availableWorkspaces !== undefined &&
        availableWorkspaces.length > 1 &&
        setWorkspaceFilter !== undefined && (
          <Select
            id={`${idPrefix}-workspace`}
            aria-label="Filter by workspace"
            filterLabel="Workspace"
            optionCounts={facetCounts.workspaceCounts}
            data-testid="products-workspace-filter"
            className="min-w-[155px]"
            value={workspaceFilter}
            onChange={(e) => {
              setWorkspaceFilter(e.target.value);
              onFilterChangeResetScroll?.();
            }}
          >
            <option value="ALL">All workspaces</option>
            {availableWorkspaces.map((ws) => (
              <option key={ws.workspaceId} value={ws.workspaceId}>
                {ws.name}
              </option>
            ))}
          </Select>
        )}
    </SearchFilterBar>
  );
}
